import { useMutation } from '@tanstack/react-query';
import {
    getFranchisePartnerLocationRequest,
    getFranchisePartnerLocationsRequest,
    updateFranchisePartnerLocationRequest
} from 'modules/franchise_partners/api';
import {
    GetFranchisePartnerLocationRequestParams,
    GetFranchisePartnerLocationsRequestParams,
    UpdateFranchisePartnerLocationRequestParams
} from 'modules/franchise_partners/types';
import { createContext, ReactElement, useState } from 'react';
import { getLocationRequest, getLocationsRequest, getStatesRequest, updateLocationRequest } from 'modules/locations/api';
import {
    GetLocationRequestParams,
    GetLocationsRequestParams,
    ILocation,
    UpdateLocationRequest,
    UpdateLocationRequestParams
} from 'modules/locations/types';

/**
 * Represents the shape of our authentication context
 */
export interface LocationsContextProps {
    getLocations: (params?: GetLocationsRequestParams) => void;
    getLocation: (params: GetLocationRequestParams) => void;
    getFranchisePartnerLocation: (params: GetFranchisePartnerLocationRequestParams) => void;
    updateLocation: ({
        pathParams,
        payload
    }: {
        pathParams: UpdateLocationRequestParams;
        payload: UpdateLocationRequest;
    }) => Promise<ILocation>;
    updateFranchisePartnerLocation: ({
        pathParams,
        payload
    }: {
        pathParams: UpdateFranchisePartnerLocationRequestParams;
        payload: UpdateLocationRequest;
    }) => Promise<ILocation>;
    locations?: ILocation[];
    isLoading: boolean;
    getFranchisePartnerLocations: (pathParams: GetFranchisePartnerLocationsRequestParams) => void;
    franchisePartnerLocations?: ILocation[];
    isFranchisePartnerLocationsLoading: boolean;
    isGetLocationLoading: boolean;
    isGetFranchisePartnerLocationLoading: boolean;
    isUpdateFranchisePartnerLocationLoading: boolean;
    isUpdateLocationLoading: boolean;
    selectedLocation?: ILocation;
    getStates: (params?: { existing?: boolean }) => void;
    states: string[];
    isStatesLoading: boolean;
}

/**
 * Initialize the context with empty function bodies.
 */
export const LocationsContext = createContext<LocationsContextProps>({
    getLocations: () => null,
    locations: [],
    isLoading: false,
    getFranchisePartnerLocations: () => null,
    getLocation: () => null,
    getFranchisePartnerLocation: () => null,
    updateLocation: async () => ({} as ILocation),
    updateFranchisePartnerLocation: async () => ({} as ILocation),
    franchisePartnerLocations: [],
    isFranchisePartnerLocationsLoading: false,
    isGetLocationLoading: false,
    isGetFranchisePartnerLocationLoading: false,
    isUpdateLocationLoading: false,
    isUpdateFranchisePartnerLocationLoading: false,
    selectedLocation: undefined,
    getStates: () => null,
    states: [],
    isStatesLoading: false
});

/**
 * Our AuthProvider component will wrap any components that need to have
 * access to, or the ability to modify, our authentication state (tokens).
 */
export const LocationsProvider = ({ children }: { children: ReactElement }): ReactElement => {
    const [selectedLocation, setSelectedLocation] = useState<ILocation>();
    const [states, setStates] = useState<string[]>([]);

    const {
        data: locations,
        mutate: getLocations,
        isPending: isLoading
    } = useMutation({
        mutationFn: getLocationsRequest
    });

    const {
        data: franchisePartnerLocations,
        mutate: getFranchisePartnerLocations,
        isPending: isFranchisePartnerLocationsLoading
    } = useMutation({ mutationFn: getFranchisePartnerLocationsRequest });

    const { mutate: getLocation, isPending: isGetLocationLoading } = useMutation({
        mutationFn: getLocationRequest,
        onSuccess: (data) => setSelectedLocation(data)
    });
    const { mutate: getFranchisePartnerLocation, isPending: isGetFranchisePartnerLocationLoading } = useMutation({
        mutationFn: getFranchisePartnerLocationRequest,
        onSuccess: (data) => setSelectedLocation(data)
    });

    const { mutateAsync: updateLocation, isPending: isUpdateLocationLoading } = useMutation({
        mutationFn: updateLocationRequest
    });
    const { mutateAsync: updateFranchisePartnerLocation, isPending: isUpdateFranchisePartnerLocationLoading } = useMutation({
        mutationFn: updateFranchisePartnerLocationRequest
    });

    const { mutate: getStates, isPending: isStatesLoading } = useMutation({
        mutationFn: getStatesRequest,
        onSuccess: (data) => setStates(data)
    });

    return (
        <LocationsContext.Provider
            value={{
                getLocations,
                locations,
                isLoading,
                getFranchisePartnerLocations,
                franchisePartnerLocations,
                isFranchisePartnerLocationsLoading,
                getLocation,
                isGetLocationLoading,
                selectedLocation,
                updateLocation,
                isUpdateLocationLoading,
                getFranchisePartnerLocation,
                isGetFranchisePartnerLocationLoading,
                updateFranchisePartnerLocation,
                isUpdateFranchisePartnerLocationLoading,
                getStates,
                states,
                isStatesLoading
            }}
        >
            {children}
        </LocationsContext.Provider>
    );
};
